/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, Stethoscope, Baby, Heart, Activity, FileText,
  MessageCircle, Bell, Shield, Share2, BookOpen, Monitor, Wand2,
  TrendingUp, ClipboardList, ChevronRight, Globe, Lock, Zap,
  Star, Menu, X, ArrowRight, CheckCircle2, Sparkles, Users, ChevronDown,
  MapPin, Palmtree,
  Send, Mic, MicOff, GraduationCap, Award, Clock, Trophy
} from 'lucide-react';
import { MODES, LANGUAGES } from '../constants/modes';
import UpgradeModal from '../components/UpgradeModal';
import { DownloadAppButton } from '../components/DownloadAppButton';

const NAV_LINKS = ['Features', 'Modes', 'Pricing', 'Masterclass', 'How It Works', 'Featured In', 'About'];

const NAV_IDS = {
  'Features': 'features',
  'Modes': 'modes',
  'Pricing': 'pricing',
  'Masterclass': 'masterclass',
  'How It Works': 'how-it-works',
  'Featured In': 'featured-in',
  'About': 'about',
};

// Oblique crossed-out price component
function CrossedPrice({ price, textColor = '#9ca3af' }) {
  return (
    <span className="relative inline-block text-2xl font-black" style={{ color: textColor }}>
      {price}
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '-4px',
        right: '-4px',
        height: '1.5px',
        background: '#ef4444',
        transform: 'translateY(-50%) rotate(-12deg)',
        borderRadius: '1px',
        pointerEvents: 'none',
        display: 'block',
      }} />
    </span>
  );
}

function scrollToSection(link, closeMenu) {
  const id = NAV_IDS[link];
  if (!id) return;
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (closeMenu) closeMenu();
  }
}

// Quick prompts for the landing page
const QUICK_PROMPTS = [
  "Emergency: Eclampsia management",
  "PCOS diagnosis criteria",
  "Cesarean section consent format",
  "GDM diet plan for pregnancy",
  "Infertility workup steps",
  "Menopause HRT options",
  "ANC first trimester checklist",
  "Fibroid treatment options",
  "Preterm labor management",
  "Endometriosis staging explained"
];

// Language codes for voice recognition
const VOICE_LANG_CODES = {
  'English': 'en-US',
  'Hindi': 'hi-IN',
  'Konkani': 'kok-IN',
  'Spanish': 'es-ES',
  'French': 'fr-FR',
  'Arabic': 'ar-SA',
  'Bengali': 'bn-IN',
  'Portuguese': 'pt-BR',
  'Russian': 'ru-RU',
  'German': 'de-DE',
  'Japanese': 'ja-JP',
  'Marathi': 'mr-IN',
  'Telugu': 'te-IN',
  'Tamil': 'ta-IN',
  'Gujarati': 'gu-IN',
  'Kannada': 'kn-IN',
  'Malayalam': 'ml-IN',
  'Punjabi': 'pa-IN',
  'Chinese (Simplified)': 'zh-CN',
  'Korean': 'ko-KR',
  'Italian': 'it-IT',
  'Dutch': 'nl-NL',
  'Turkish': 'tr-TR',
  'Thai': 'th-TH',
  'Vietnamese': 'vi-VN',
  'Indonesian': 'id-ID',
  'Urdu': 'ur-PK',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isRecording, setIsRecording] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [goaOpen, setGoaOpen] = useState(false);
  const [goaImg, setGoaImg] = useState(0);
  const GOA_POSTERS = ['/goa-immersive-poster-1.jpg', '/goa-immersive-poster-2.jpg'];
  const GOA_START = new Date('2026-12-04T09:00:00+05:30').getTime();
  const [goaLeft, setGoaLeft] = useState(GOA_START - Date.now());
  useEffect(() => {
    const t = setInterval(() => setGoaLeft(GOA_START - Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const cd = (() => {
    const ms = Math.max(0, goaLeft);
    return {
      d: Math.floor(ms / 86400000),
      h: Math.floor((ms % 86400000) / 3600000),
      m: Math.floor((ms % 3600000) / 60000),
      s: Math.floor((ms % 60000) / 1000),
    };
  })();
  const GoaCountdown = ({ compact }) => (
    <div className={`flex items-center ${compact ? 'gap-1' : 'gap-1.5'}`} data-testid={compact ? 'goa-countdown-mobile' : 'goa-countdown'}>
      {[[cd.d, 'Days'], [cd.h, 'Hrs'], [cd.m, 'Min'], [cd.s, 'Sec']].map(([v, l], i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className={`font-black text-amber-400/60 ${compact ? 'text-[10px]' : 'text-xs'}`}>:</span>}
          <div className={`text-center rounded-lg bg-black/40 border border-amber-400/30 ${compact ? 'px-1.5 py-0.5 min-w-[30px]' : 'px-2 py-1 min-w-[40px]'}`}>
            <p className={`font-black text-amber-300 leading-none tabular-nums ${compact ? 'text-xs' : 'text-base'}`}>{String(v).padStart(2, '0')}</p>
            <p className={`font-bold uppercase tracking-wider text-amber-200/60 leading-none mt-0.5 ${compact ? 'text-[6px]' : 'text-[7px]'}`}>{l}</p>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const handleSubscribe = () => {
    const signed = localStorage.getItem('gynovate_signup') ||
      document.cookie.split(';').some(c => c.trim().startsWith('gynovate_signed='));
    if (!signed) {
      navigate('/signup');
    } else {
      setUpgradeOpen(true);
    }
  };
  const recognitionRef = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownOpen && !e.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
      }
      if (showLangDropdown && !e.target.closest('.lang-dropdown')) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen, showLangDropdown]);

  // Handle chat submission from landing page
  const handleChatSubmit = (message) => {
    const query = message || chatInput;
    if (query.trim()) {
      // Navigate to chat with the initial message and language as query params
      navigate(`/chat?q=${encodeURIComponent(query.trim())}&lang=${encodeURIComponent(selectedLanguage)}`);
    }
  };

  // Voice input functions
  const startVoiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice input not supported. Please use Chrome, Edge, or Safari.');
      return;
    }

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => {
        const recognition = new SR();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = VOICE_LANG_CODES[selectedLanguage] || 'en-US';
        
        recognition.onresult = (e) => {
          const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
          setChatInput(transcript);
        };
        
        recognition.onend = () => setIsRecording(false);
        
        recognition.onerror = (e) => {
          setIsRecording(false);
          if (e.error === 'not-allowed') {
            toast.error('Microphone access denied. Please allow microphone permission.');
          } else if (e.error === 'no-speech') {
            toast.info('No speech detected. Please try again.');
          } else {
            toast.error(`Voice error: ${e.error}`);
          }
        };
        
        recognition.start();
        recognitionRef.current = recognition;
        setIsRecording(true);
        toast.info(`Listening in ${selectedLanguage}... speak now`);
      })
      .catch(() => {
        toast.error('Microphone access denied. Please allow microphone permission.');
      });
  };

  const stopVoiceInput = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  return (
    <div className="min-h-screen bg-bone font-body overflow-x-hidden">
      {/* Navbar */}
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-neutral-950/90 backdrop-blur-xl shadow-lg border-b border-amber-400/15' : 'bg-neutral-950/60 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="https://customer-assets.emergentagent.com/job_clinical-gyn/artifacts/598nprae_Gynovate%20Logo.jpg"
              alt="Gynovate AI/SI"
              className="h-10 w-10 rounded-xl object-cover"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold font-heading text-white tracking-tight">Gynovate AI/SI<sup className="text-[8px] align-super text-amber-400">TM</sup></span>
              <span className="text-[9px] text-amber-200/60 font-medium tracking-wide hidden sm:block">Built in Goa, Bharat by Dr. Vikram Dukle</span>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(link => (
              <button
                key={link}
                onClick={() => scrollToSection(link)}
                className="text-sm text-neutral-300 hover:text-amber-300 font-medium transition-colors duration-200 cursor-pointer"
              >
                {link}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Download App — visible to all pre-login visitors */}
            <DownloadAppButton variant="light" testid="landing-download-app-btn" />

            {/* Dropdown Launch Button */}
            <div className="relative dropdown-container hidden md:block">
              <button
                data-testid="nav-launch-dropdown"
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
                className="flex items-center gap-2 px-5 py-2.5 text-white rounded-full text-sm font-bold hover:opacity-90 transition-opacity duration-200 shadow-[0_6px_20px_rgba(225,29,72,0.35)]"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #E11D48 50%, #F59E0B 100%)' }}
              >
                Launch <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-fade-in">
                  <button
                    onClick={() => { navigate('/signup'); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-raspberry-50 transition-colors text-left"
                    data-testid="dropdown-ai-assistant"
                  >
                    <div className="w-9 h-9 bg-raspberry-100 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-raspberry-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">AI Assistant</p>
                      <p className="text-xs text-gray-500">AI/SI Copilot for Doctors</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { navigate('/network'); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-raspberry-50 transition-colors text-left"
                    data-testid="dropdown-network"
                  >
                    <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Gynovate Network</p>
                      <p className="text-xs text-gray-500">Connect with Doctors</p>
                    </div>
                  </button>
                </div>
              )}
            </div>
            <button
              className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-neutral-950/95 backdrop-blur-xl border-t border-amber-400/15 px-6 py-4 space-y-3">
            {NAV_LINKS.map(link => (
              <button
                key={link}
                onClick={() => scrollToSection(link, () => setMenuOpen(false))}
                className="block w-full text-left text-sm text-neutral-200 font-medium py-2 cursor-pointer"
              >
                {link}
              </button>
            ))}
            <div className="pt-2 space-y-2">
              <button
                onClick={() => navigate('/signup')}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-white rounded-full text-sm font-bold"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #E11D48 50%, #F59E0B 100%)' }}
              >
                <Sparkles className="w-4 h-4" />
                AI Assistant
              </button>
              <button
                onClick={() => navigate('/network')}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-full text-sm font-semibold"
              >
                <Users className="w-4 h-4" />
                Gynovate Network
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero — Vibrant Gynovate yellow · red · black */}
      <section
        id="features"
        className="relative pt-28 pb-20 overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at 18% 0%, rgba(225,29,72,0.25), transparent 55%), radial-gradient(ellipse at 85% 12%, rgba(253,207,51,0.16), transparent 52%), #0F0F11' }}
      >
        {/* Vibrant light leaks */}
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none animate-aura-pulse" style={{ background: '#E11D48' }} />
        <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none animate-aura-pulse" style={{ background: '#FDCF33', animationDelay: '2s' }} />
        <div className="absolute top-1/3 left-1/3 w-72 h-72 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: '#DC2626' }} />

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full shadow-sm border border-amber-400/40 bg-amber-400/10 backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-xs font-semibold text-amber-300 tracking-[0.18em] uppercase">AI/SI Copilot for Doctors</span>
              </div>

              <h1 className="font-heading font-extrabold leading-[1.02] tracking-tight text-white">
                <span className="text-5xl md:text-7xl block bg-gradient-to-br from-[#FDE047] via-[#FDCF33] to-[#F59E0B] bg-clip-text text-transparent drop-shadow-[0_2px_12px_rgba(253,207,51,0.25)]">Gynovate AI/SI<sup className="text-lg md:text-2xl align-super text-[#DC2626]">TM</sup></span>
                <span className="text-4xl md:text-6xl block text-white">Your AI/SI</span>
                <span
                  className="text-5xl md:text-7xl block font-accent italic"
                  style={{ color: '#FDCF33' }}
                >
                  Copilot
                </span>
                <span className="text-4xl md:text-6xl block text-white">for Doctors</span>
              </h1>

              <p className="inline-flex items-center gap-2 text-sm md:text-base font-black tracking-[0.22em] uppercase bg-gradient-to-r from-[#FDCF33] via-[#F59E0B] to-[#DC2626] bg-clip-text text-transparent" data-testid="hero-superapp-line">
                The Complete Super App
              </p>

              {/* Live social proof — 1,367 doctors */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/[0.07] backdrop-blur-md border border-amber-400/25 shadow-sm" data-testid="hero-social-proof">
                <span className="text-base leading-none" aria-hidden="true">🇮🇳</span>
                <span className="flex -space-x-1.5">
                  {['#fbbf24', '#f87171', '#a78bfa', '#34d399'].map((c, i) => (
                    <span key={i} className="w-5 h-5 rounded-full border-2 border-neutral-900 shadow-sm" style={{ background: c }} />
                  ))}
                </span>
                <span className="text-sm font-semibold text-neutral-300">
                  <span className="font-extrabold text-white">2,490</span> doctors using Gynovate
                </span>
                <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>

              {/* Just Launched — Gynovate MD & Gynovate Social */}
              <div
                className="relative overflow-hidden flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-md shadow-sm max-w-xl"
                data-testid="just-launched-banner"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-amber-400/15 to-transparent pointer-events-none animate-[shimmer_3.5s_ease-in-out_infinite]" />
                <span className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest uppercase text-neutral-900 shadow-sm" style={{ background: 'linear-gradient(135deg, #FDCF33, #F59E0B)' }}>
                  🚀 Just Launched
                </span>
                <a
                  href="/md"
                  className="relative inline-flex items-center gap-1.5 text-sm font-bold text-teal-300 hover:text-teal-200 transition-colors"
                  data-testid="just-launched-md-link"
                >
                  <span className="w-2 h-2 rounded-full bg-teal-400" /> Gynovate MD <span className="text-white/40 font-medium hidden sm:inline">— every speciality</span> →
                </a>
                <a
                  href="/social"
                  className="relative inline-flex items-center gap-1.5 text-sm font-bold text-rose-400 hover:text-rose-300 transition-colors"
                  data-testid="just-launched-social-link"
                >
                  <span className="w-2 h-2 rounded-full bg-rose-500" /> Gynovate Social <span className="text-white/40 font-medium hidden sm:inline">— AI social manager</span> →
                </a>
              </div>

              {/* Primary CTA — immediately after headline */}
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/signup')}
                  style={{ background: 'linear-gradient(135deg, #DC2626 0%, #E11D48 50%, #F59E0B 100%)', color: '#fff', boxShadow: '0 10px 30px rgba(225,29,72,0.4)' }}
                  className="flex items-center gap-2 px-8 py-4 rounded-full text-base font-bold transition-all ring-2 ring-amber-400/30 hover:opacity-90 hover:ring-amber-400/60"
                >
                  Get Started Free
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => scrollToSection('Modes')}
                  className="flex items-center gap-2 text-sm font-medium text-amber-300 hover:text-amber-200 transition-colors"
                >
                  Explore 51+ Modes <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Trust badges — soft pastel pills (Apple Health calm) */}
              <div className="flex flex-wrap gap-2" data-testid="hero-trust-badges">
                <span className="gx-trust-pill"><Stethoscope className="w-3 h-3" />Built by an OB-GYN</span>
                <span className="gx-trust-pill cream"><Sparkles className="w-3 h-3" />Made in India</span>
                <span className="gx-trust-pill lavender"><Shield className="w-3 h-3" />HIPAA-conscious</span>
                <span className="gx-trust-pill"><Wand2 className="w-3 h-3" />51+ Clinical Modes</span>
              </div>

              {/* MOBILE-FIRST: Premium Masterclass Spotlight — shown right after CTA on phones for first-look visibility */}
              <div className="lg:hidden">
                <a
                  href="https://payments.cashfree.com/forms/gynovate-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shimmer-sweep relative block rounded-3xl overflow-hidden shadow-2xl border border-amber-400/40"
                  style={{
                    background:
                      'radial-gradient(at 14% 14%, rgba(251,191,36,0.22), transparent 50%), radial-gradient(at 86% 86%, rgba(192,18,60,0.22), transparent 50%), linear-gradient(160deg, #0f0a1e 0%, #1a0e2e 55%, #2a1233 100%)',
                  }}
                  data-testid="hero-masterclass-spotlight-mobile"
                >
                  <div
                    className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
                    style={{
                      backgroundImage:
                        'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
                    }}
                  />
                  <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-400/25 blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-14 -left-14 w-52 h-52 rounded-full bg-raspberry-500/25 blur-3xl pointer-events-none" />

                  <div className="relative p-5">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/40">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                        <span className="text-[9px] font-black tracking-[0.16em] uppercase text-amber-200">Flagship Live Course</span>
                      </div>
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                        <Trophy className="w-3 h-3 text-amber-300" />
                        <span className="text-[9px] font-black tracking-wider uppercase text-amber-200">Hands-On</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative flex-shrink-0">
                        <span className="absolute inset-0 rounded-full bg-amber-300/30 blur-2xl scale-150 animate-aura-pulse" />
                        <div
                          className="relative w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl border border-amber-300/40"
                          style={{
                            background:
                              'conic-gradient(from 210deg at 50% 50%, #f59e0b 0deg, #fbbf24 80deg, #fde68a 160deg, #f59e0b 240deg, #d97706 320deg, #f59e0b 360deg)',
                          }}
                        >
                          <div className="absolute inset-1 rounded-[0.95rem] bg-gradient-to-br from-amber-50 via-white to-amber-100 flex items-center justify-center overflow-hidden">
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-amber-200/50 to-transparent pointer-events-none animate-[shimmer_4s_ease-in-out_infinite]" />
                            <Sparkles className="relative w-9 h-9 text-raspberry-800 animate-[cta-pulse_2.6s_ease-in-out_infinite]" strokeWidth={1.4} fill="currentColor" />
                          </div>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-300/30 mb-1.5">
                          <span className="w-1 h-1 rounded-full bg-amber-300 animate-pulse" />
                          <span className="text-[9px] font-black tracking-[0.18em] uppercase text-amber-200">Hands-On · Live · AI</span>
                        </div>
                        <h3 className="text-xl font-extrabold font-heading text-white leading-[1.05] tracking-tight">
                          Lead the AI Era,<br />
                          <span className="font-accent italic text-amber-300">in just 2 days.</span>
                        </h3>
                      </div>
                    </div>

                    <ul className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { icon: Clock, label: 'Save 10+ hrs/wk' },
                        { icon: Stethoscope, label: 'Sharper Dx' },
                        { icon: Award, label: 'Hands-on workflows' },
                        { icon: Users, label: 'Live cohort' },
                      ].map(({ icon: Icon, label }) => (
                        <li key={label} className="flex items-center gap-1.5 text-[11px] text-white/85 font-medium">
                          <span className="w-5 h-5 rounded-md bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                            <Icon className="w-3 h-3 text-amber-300" />
                          </span>
                          {label}
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm mb-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-black tracking-widest uppercase text-amber-200">Next Live Batch</p>
                        <p className="text-sm font-bold text-white leading-tight mt-0.5">31st October &amp; 1st November 2026</p>
                        <p className="text-[10px] text-white/55 mt-0.5">Online on Zoom · Live + Recorded</p>
                      </div>
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30">
                        <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
                        <span className="text-[9px] font-black uppercase tracking-wider text-red-300">Limited</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 mb-4 text-[10px] text-white/65">
                      <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1">
                          {['#fbbf24', '#f87171', '#a78bfa', '#34d399'].map((c, i) => (
                            <span key={i} className="w-4 h-4 rounded-full border border-slate-900" style={{ background: c }} />
                          ))}
                        </div>
                        <span><span className="font-bold text-white">1000+</span> doctors</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="flex">
                          {[0,1,2,3,4].map(i => <Star key={i} className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />)}
                        </div>
                        <span><span className="font-bold text-white">4.9</span>/5 · 400+</span>
                      </div>
                    </div>

                    <div
                      className="shimmer-sweep relative inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl text-sm font-black text-amber-950 shadow-xl shadow-amber-900/40 overflow-hidden animate-[cta-pulse_2.4s_ease-in-out_infinite]"
                      style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)' }}
                    >
                      <GraduationCap className="w-4 h-4 relative z-[1]" />
                      <span className="relative z-[1]">Reserve My Seat</span>
                      <ArrowRight className="w-4 h-4 relative z-[1]" />
                    </div>
                    <p className="text-[10px] text-white/45 text-center mt-2">
                      Built by <span className="text-white/70 font-semibold">Dr. Vikram Dukle</span> · AI Generalist
                    </p>
                  </div>
                </a>

                {/* Goa 3-Day Offline Immersive — mobile */}
                <div
                  className="relative overflow-hidden rounded-2xl border border-amber-300/30 shadow-lg p-4 mt-3"
                  style={{ background: 'linear-gradient(115deg, #1a1205 0%, #2a1c05 55%, #3a1408 100%)' }}
                  data-testid="goa-hero-card-mobile"
                >
                  <div className="flex items-start gap-3">
                    <button onClick={() => { setGoaImg(0); setGoaOpen(true); }} className="w-16 shrink-0" data-testid="goa-hero-thumb-mobile">
                      <img src={GOA_POSTERS[0]} alt="Goa immersive course" className="w-full rounded-lg border border-amber-300/30 shadow-md" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-black tracking-[0.14em] uppercase text-amber-200">🌴 Offline · Candolim, Goa</p>
                      <p className="text-sm font-black text-white leading-tight mt-0.5">4th – 6th Dec 2026 · 3-Day Immersive</p>
                      <p className="text-[10px] text-white/60 mt-0.5">Experience an AI Transformation at Goa</p>
                      <div className="mt-2"><GoaCountdown compact /></div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => { setGoaImg(0); setGoaOpen(true); }}
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-amber-950"
                          style={{ background: 'linear-gradient(135deg, #fbbf24, #d97706)' }}
                        >
                          View →
                        </button>
                        <a
                          href="https://wa.me/919850745456?text=Hi!%20I%20want%20to%20register%20for%20GYNOVATE%20AI%202026%20Immersive%20Course%20(4-6%20December%2C%20Goa)"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-white"
                          style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                          data-testid="goa-whatsapp-btn-mobile"
                        >
                          WhatsApp to Register
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ✦ JUST LAUNCHED showcase — mobile */}
                <div
                  className="relative overflow-hidden rounded-3xl p-4 border border-white/10 shadow-xl mt-3"
                  style={{ background: 'linear-gradient(150deg, #0d1220 0%, #131a2e 55%, #0f1526 100%)' }}
                  data-testid="just-launched-showcase-mobile"
                >
                  <div className="absolute inset-0 pointer-events-none opacity-60" style={{ backgroundImage: 'radial-gradient(circle at 15% 0%, rgba(45,212,191,0.16), transparent 40%), radial-gradient(circle at 85% 100%, rgba(251,113,133,0.14), transparent 40%)' }} />
                  <div className="relative flex items-center gap-2.5 mb-3">
                    <span className="relative overflow-hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase text-white shadow-md" style={{ background: 'linear-gradient(90deg, #f59e0b, #fb7185, #2dd4bf)' }}>
                      <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_2.6s_ease-in-out_infinite] pointer-events-none" />
                      <span className="relative">🚀 Just Launched</span>
                    </span>
                    <span className="flex-1 h-px bg-gradient-to-r from-white/25 to-transparent" />
                  </div>
                  <div className="relative space-y-2">
                    <a href="/md" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-teal-400/10" data-testid="md-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #0ea5a4, #0369a1)' }}>
                        <span className="text-base">🩺</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Gynovate MD</p>
                        <p className="text-[10px] text-slate-400 leading-snug">Every speciality · Indian guidelines</p>
                      </div>
                      <span className="text-teal-300 text-sm font-bold">&rarr;</span>
                    </a>
                    <a href="/social" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-rose-400/10" data-testid="social-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #c0123c, #7a0e2e)' }}>
                        <img src="/gynovate-social-logo.png" alt="" className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Gynovate Social</p>
                        <p className="text-[10px] text-slate-400 leading-snug">AI social manager · all doctors</p>
                      </div>
                      <span className="text-rose-300 text-sm font-bold">&rarr;</span>
                    </a>
                    <a href="/personal" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-amber-400/10" data-testid="personal-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                        <span className="text-base">🌟</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Gynovate Personal</p>
                        <p className="text-[10px] text-slate-400 leading-snug">Communication · leadership · new skills</p>
                      </div>
                      <span className="text-amber-300 text-sm font-bold">&rarr;</span>
                    </a>
                    <a href="/2030" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-cyan-400/10" data-testid="g2030-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #06b6d4, #4f46e5)' }}>
                        <span className="text-base">🚀</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Gynovate 2030</p>
                        <p className="text-[10px] text-slate-400 leading-snug">Imagine & future-proof your practice</p>
                      </div>
                      <span className="text-cyan-300 text-sm font-bold">&rarr;</span>
                    </a>
                    <a href="/gia" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-violet-400/10" data-testid="gia-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #6d28d9, #db2777)' }}>
                        <span className="text-base">💜</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Talk to Gia</p>
                        <p className="text-[10px] text-slate-400 leading-snug">Mental wellness coach for doctors</p>
                      </div>
                      <span className="text-violet-300 text-sm font-bold">&rarr;</span>
                    </a>
                    <a href="/health" className="flex items-center gap-3 rounded-2xl p-3 bg-white/[0.05] border border-white/10 active:bg-emerald-400/10" data-testid="health-cta-btn-mobile">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: 'linear-gradient(135deg, #3a7d5d, #2E4A3B)' }}>
                        <span className="text-base">🌿</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-black text-white leading-tight">Gynovate Health</p>
                        <p className="text-[10px] text-slate-400 leading-snug">Your health. Your dashboard.</p>
                      </div>
                      <span className="text-emerald-300 text-sm font-bold">&rarr;</span>
                    </a>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Evidence-based decision support, clinical documentation, patient communication,
                and practice growth — all in one platform created by{' '}
                <strong className="font-bold" style={{ color: '#5D4037' }}>
                  India's leading Cosmetic Gynaecologist and Innovator, Dr. Vikram Dukle from Candolim, Goa – India.
                </strong>
              </p>

              {/* Chat Input - Like ChatGPT */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-2 max-w-xl">
                {/* Language selector row */}
                <div className="flex items-center justify-between px-2 pb-2 border-b border-gray-100 mb-2">
                  <span className="text-xs text-gray-400">Ask in any language</span>
                  <div className="relative lang-dropdown">
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowLangDropdown(!showLangDropdown); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      {selectedLanguage}
                      <ChevronDown className={`w-3 h-3 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showLangDropdown && (
                      <div className="absolute right-0 mt-1 w-48 max-h-64 overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                        {Object.keys(VOICE_LANG_CODES).map(lang => (
                          <button
                            key={lang}
                            onClick={() => { setSelectedLanguage(lang); setShowLangDropdown(false); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedLanguage === lang ? 'text-raspberry-700 font-medium bg-raspberry-50' : 'text-gray-700'}`}
                          >
                            {lang}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Input row */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                    placeholder="What can I help you with today?"
                    className="flex-1 px-4 py-3.5 text-base text-gray-800 placeholder-gray-400 bg-transparent focus:outline-none"
                    data-testid="landing-chat-input"
                  />
                  {/* Voice button */}
                  <button
                    onClick={isRecording ? stopVoiceInput : startVoiceInput}
                    className={`p-3 rounded-xl transition-colors ${
                      isRecording 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={isRecording ? 'Stop recording' : `Speak in ${selectedLanguage}`}
                  >
                    {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                  {/* Send button */}
                  <button
                    onClick={() => handleChatSubmit()}
                    disabled={!chatInput.trim()}
                    className="p-3 bg-raspberry-800 text-white rounded-xl hover:bg-raspberry-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="landing-chat-submit"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Quick Prompts */}
                <div className="flex flex-wrap gap-2 px-2 pb-2 pt-3 border-t border-gray-100 mt-2">
                  {QUICK_PROMPTS.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleChatSubmit(prompt)}
                      className="text-xs px-3 py-1.5 bg-gray-50 hover:bg-raspberry-50 text-gray-600 hover:text-raspberry-700 rounded-full border border-gray-200 hover:border-raspberry-200 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clinical Disclaimer */}
              <div className="relative">
                <p className="text-sm italic text-gray-500 font-light tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                  <span className="text-raspberry-400">"</span>
                  AI outputs are decision support, not a replacement for clinical judgement.
                  <span className="text-raspberry-400">"</span>
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3 pt-2">
                {['WHO Guidelines', 'ACOG/RCOG', 'FOGSI/FIGO', 'Medico-Legal Safe'].map(badge => (
                  <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-600 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Mobile spotlight is now inside the left column above (for first-look visibility) */}

            {/* Right: Premium Masterclass Spotlight (replaces hero photo — moved near Founder Story) */}
            <div className="relative hidden lg:flex flex-col gap-3">
              {/* Masterclass quick CTA — full width */}
              <a
                href="https://payments.cashfree.com/forms/gynovate-registration"
                target="_blank"
                rel="noopener noreferrer"
                className="relative overflow-hidden flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-lg transition-all group"
                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)' }}
                data-testid="masterclass-cta-btn"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none animate-[shimmer_3s_ease-in-out_infinite_0.5s]" />
                <div className="relative w-9 h-9 rounded-xl bg-white/25 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <span className="text-base">🎓</span>
                </div>
                <div className="relative min-w-0">
                  <p className="text-[10px] font-black tracking-widest uppercase text-amber-100 leading-none">AI Masterclass</p>
                  <p className="text-sm font-bold text-white leading-tight mt-0.5 truncate">Join Course &rarr;</p>
                </div>
              </a>

              {/* ✦ JUST LAUNCHED showcase — MD · Social · Gia */}
              <div
                className="relative overflow-hidden rounded-3xl p-4 border border-white/10 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.55)]"
                style={{ background: 'linear-gradient(150deg, #0d1220 0%, #131a2e 55%, #0f1526 100%)' }}
                data-testid="just-launched-showcase"
              >
                <div className="absolute inset-0 pointer-events-none opacity-60" style={{ backgroundImage: 'radial-gradient(circle at 15% 0%, rgba(45,212,191,0.16), transparent 40%), radial-gradient(circle at 85% 100%, rgba(251,113,133,0.14), transparent 40%)' }} />
                <div className="relative flex items-center gap-2.5 mb-3">
                  <span className="relative overflow-hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase text-white shadow-md" style={{ background: 'linear-gradient(90deg, #f59e0b, #fb7185, #2dd4bf)' }}>
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_2.6s_ease-in-out_infinite] pointer-events-none" />
                    <span className="relative">🚀 Just Launched</span>
                  </span>
                  <span className="flex-1 h-px bg-gradient-to-r from-white/25 to-transparent" />
                </div>
                <div className="relative grid grid-cols-2 gap-2.5">
                  <a
                    href="/md"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-teal-300/50 hover:bg-teal-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="md-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #0ea5a4, #0369a1)' }}>
                      <span className="text-base">🩺</span>
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Gynovate MD</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">Every speciality · Indian guidelines</p>
                    <p className="text-[11px] font-bold text-teal-300 mt-2 group-hover:translate-x-0.5 transition-transform">Ask now &rarr;</p>
                  </a>
                  <a
                    href="/social"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-rose-300/50 hover:bg-rose-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="social-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #c0123c, #7a0e2e)' }}>
                      <img src="/gynovate-social-logo.png" alt="" className="w-5 h-5" />
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Gynovate Social</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">AI social manager · all doctors</p>
                    <p className="text-[11px] font-bold text-rose-300 mt-2 group-hover:translate-x-0.5 transition-transform">Create &rarr;</p>
                  </a>
                  <a
                    href="/personal"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-amber-300/50 hover:bg-amber-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="personal-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                      <span className="text-base">🌟</span>
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Gynovate Personal</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">Communication · leadership · new skills</p>
                    <p className="text-[11px] font-bold text-amber-300 mt-2 group-hover:translate-x-0.5 transition-transform">Grow &rarr;</p>
                  </a>
                  <a
                    href="/2030"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-cyan-300/50 hover:bg-cyan-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="g2030-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #06b6d4, #4f46e5)' }}>
                      <span className="text-base">🚀</span>
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Gynovate 2030</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">Imagine & future-proof your practice</p>
                    <p className="text-[11px] font-bold text-cyan-300 mt-2 group-hover:translate-x-0.5 transition-transform">Explore &rarr;</p>
                  </a>
                  <a
                    href="/gia"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-violet-300/50 hover:bg-violet-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="gia-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #6d28d9, #db2777)' }}>
                      <span className="text-base">💜</span>
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Talk to Gia</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">Mental wellness coach for doctors</p>
                    <p className="text-[11px] font-bold text-violet-300 mt-2 group-hover:translate-x-0.5 transition-transform">Talk &rarr;</p>
                  </a>
                  <a
                    href="/health"
                    className="group rounded-2xl p-3 bg-white/[0.05] border border-white/10 hover:border-emerald-300/50 hover:bg-emerald-400/10 hover:-translate-y-[2px] hover:shadow-xl transition-transform duration-200"
                    data-testid="health-cta-btn"
                  >
                    <div className="w-9 h-9 rounded-xl mb-2.5 flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #3a7d5d, #2E4A3B)' }}>
                      <span className="text-base">🌿</span>
                    </div>
                    <p className="text-[13px] font-black text-white leading-tight">Gynovate Health</p>
                    <p className="text-[10px] text-slate-400 leading-snug mt-1">Your health. Your dashboard.</p>
                    <p className="text-[11px] font-bold text-emerald-300 mt-2 group-hover:translate-x-0.5 transition-transform">Start &rarr;</p>
                  </a>
                </div>
              </div>

              {/* Premium Masterclass Spotlight Card */}
              <a
                href="https://payments.cashfree.com/forms/gynovate-registration"
                target="_blank"
                rel="noopener noreferrer"
                className="shimmer-sweep relative rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(192,18,60,0.45)] border border-amber-400/40 group cursor-pointer block"
                style={{
                  background:
                    'radial-gradient(at 18% 14%, rgba(251,191,36,0.22), transparent 50%), radial-gradient(at 80% 86%, rgba(192,18,60,0.22), transparent 50%), radial-gradient(at 50% 50%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(160deg, #0a0613 0%, #150a26 35%, #20102d 70%, #2a1233 100%)',
                  minHeight: '560px',
                }}
                data-testid="hero-masterclass-spotlight"
              >
                {/* Decorative grain overlay */}
                <div
                  className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
                  style={{
                    backgroundImage:
                      'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
                  }}
                />
                {/* Floating glow */}
                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-raspberry-500/20 blur-3xl pointer-events-none" />

                <div className="relative p-7 flex flex-col h-full" style={{ minHeight: '520px' }}>
                  {/* Top: status pill */}
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                      <span className="text-[10px] font-black tracking-[0.18em] uppercase text-amber-200">Flagship Live Course</span>
                    </div>
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                      <Trophy className="w-3 h-3 text-amber-300" />
                      <span className="text-[10px] font-black tracking-wider uppercase text-amber-200">Hands-On</span>
                    </div>
                  </div>

                  {/* Center: Big shimmering medallion */}
                  <div className="flex-1 flex flex-col items-center justify-center py-6">
                    <div className="relative">
                      {/* Rotating aurora ring */}
                      <span className="absolute inset-0 rounded-full bg-amber-300/30 blur-3xl scale-150 animate-aura-pulse" />
                      <span className="absolute inset-0 rounded-full bg-raspberry-500/20 blur-2xl scale-125 animate-aura-pulse" style={{ animationDelay: '1s' }} />
                      {/* Spinning conic gradient ring */}
                      <div
                        className="relative w-40 h-40 rounded-[2rem] flex items-center justify-center shadow-[0_0_60px_-10px_rgba(251,191,36,0.5)] border border-amber-300/50"
                        style={{
                          background:
                            'conic-gradient(from 210deg at 50% 50%, #f59e0b 0deg, #fbbf24 80deg, #fde68a 160deg, #f59e0b 240deg, #d97706 320deg, #f59e0b 360deg)',
                        }}
                      >
                        {/* Inner medallion */}
                        <div className="absolute inset-1.5 rounded-[1.75rem] bg-gradient-to-br from-amber-50 via-white to-amber-100 flex flex-col items-center justify-center overflow-hidden">
                          {/* Inner shimmer sweep */}
                          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-amber-200/50 to-transparent pointer-events-none animate-[shimmer_4s_ease-in-out_infinite]" />
                          <Sparkles className="relative w-14 h-14 text-raspberry-800 drop-shadow animate-[cta-pulse_2.6s_ease-in-out_infinite]" strokeWidth={1.4} fill="currentColor" />
                          <p className="relative mt-1.5 text-[10px] font-black tracking-[0.18em] uppercase text-raspberry-800">Masterclass</p>
                        </div>
                        {/* Floating sparkle decorations */}
                        <span className="absolute -top-2 left-3 text-amber-300 text-lg animate-soft-float" aria-hidden="true">✦</span>
                        <span className="absolute -bottom-1 right-2 text-amber-200 text-sm animate-soft-float" style={{ animationDelay: '2s' }} aria-hidden="true">✦</span>
                      </div>
                    </div>

                    {/* Eyebrow */}
                    <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-amber-300/30 backdrop-blur-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                      <span className="text-[10px] font-black tracking-[0.22em] uppercase text-amber-200">Hands-On · Live · AI</span>
                    </div>

                    <h3 className="mt-3 text-3xl font-extrabold font-heading text-white text-center leading-[1.05] tracking-tight">
                      Lead the AI Era,<br />
                      <span className="font-accent italic text-amber-300">in just 2 days.</span>
                    </h3>
                    <p className="mt-3 text-sm text-white/70 text-center max-w-[300px] leading-relaxed">
                      A hands-on, live AI workshop built for doctors who want to lead the next era of clinical practice.
                    </p>
                  </div>

                  {/* Bottom: date + CTA */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black tracking-widest uppercase text-amber-200">Next Live Batch</p>
                        <p className="text-base font-bold text-white leading-tight mt-0.5">31st October &amp; 1st November 2026</p>
                        <p className="text-[10px] text-white/55 mt-0.5">Online on Zoom · Live + Recorded</p>
                      </div>
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30">
                        <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
                        <span className="text-[9px] font-black uppercase tracking-wider text-red-300">Limited</span>
                      </div>
                    </div>
                    <div
                      className="shimmer-sweep relative inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-2xl text-sm font-black text-amber-950 shadow-xl shadow-amber-900/40 overflow-hidden animate-[cta-pulse_2.6s_ease-in-out_infinite]"
                      style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)' }}
                    >
                      <GraduationCap className="w-4 h-4 relative z-[1]" />
                      <span className="relative z-[1]">Reserve My Seat</span>
                      <ArrowRight className="w-4 h-4 relative z-[1] group-hover:translate-x-1 transition-transform" />
                    </div>
                    <p className="text-[11px] text-white/45 text-center">
                      Built by <span className="text-white/70 font-semibold">Dr. Vikram Dukle</span> · AI Generalist
                    </p>
                  </div>
                </div>
              </a>

              {/* Goa 3-Day Offline Immersive — below the Sept online batch */}
              <div
                className="relative overflow-hidden rounded-3xl border border-amber-300/30 shadow-xl p-5"
                style={{ background: 'linear-gradient(115deg, #1a1205 0%, #2a1c05 55%, #3a1408 100%)' }}
                data-testid="goa-hero-card"
              >
                <div className="absolute inset-0 pointer-events-none opacity-70" style={{ backgroundImage: 'radial-gradient(circle at 90% 15%, rgba(245,158,11,0.28), transparent 45%)' }} />
                <div className="relative flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 mb-2">
                      <span className="text-xs">🌴</span>
                      <span className="text-[9px] font-black tracking-[0.14em] uppercase text-amber-200">Offline · Candolim, Goa</span>
                    </div>
                    <p className="text-base font-black text-white leading-tight">4th – 6th December 2026</p>
                    <p className="text-[11px] text-white/60 mt-0.5">3-Day Immersive AI Course · Experience an AI Transformation at Goa</p>
                    <div className="mt-2.5"><GoaCountdown /></div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => { setGoaImg(0); setGoaOpen(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-amber-950 transition-opacity hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #fbbf24, #d97706)' }}
                        data-testid="goa-hero-details-btn"
                      >
                        View Course →
                      </button>
                      <a
                        href="https://wa.me/919850745456?text=Hi!%20I%20want%20to%20register%20for%20GYNOVATE%20AI%202026%20Immersive%20Course%20(4-6%20December%2C%20Goa)"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-white transition-opacity hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                        data-testid="goa-hero-whatsapp-btn"
                      >
                        WhatsApp 9850745456
                      </a>
                    </div>
                  </div>
                  <div className="relative w-24 shrink-0">
                    <button onClick={() => { setGoaImg(1); setGoaOpen(true); }} className="absolute -left-6 top-2 w-full" data-testid="goa-hero-thumb-2">
                      <img src={GOA_POSTERS[1]} alt="Goa course details" className="w-full rounded-lg border border-white/10 shadow-lg rotate-[-6deg] opacity-90" />
                    </button>
                    <button onClick={() => { setGoaImg(0); setGoaOpen(true); }} className="relative block w-full" data-testid="goa-hero-thumb-1">
                      <img src={GOA_POSTERS[0]} alt="Goa immersive course poster" className="w-full rounded-lg border border-amber-300/30 shadow-xl rotate-[4deg]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-raspberry-800 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            {[
              { value: '51+', label: 'Clinical Modes' },
              { value: '60+', label: 'World Languages' },
              { value: '11+', label: 'Guideline Bodies' },
              { value: '2026', label: 'Latest Guidelines' },
            ].map(stat => (
              <div key={stat.label}>
                <div className="text-3xl font-bold font-heading mb-1">{stat.value}</div>
                <div className="text-sm text-raspberry-100 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mode Showcase - 5 Categories - Dropdown Style */}
      <section id="modes" className="py-24 gx-pastel-ambient relative overflow-hidden">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-lavender-soft rounded-full blur-3xl opacity-40 pointer-events-none" />
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-dawn-rose rounded-full blur-3xl opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <p className="gx-eyebrow mb-3">Every Clinical Need</p>
            <h2 className="text-4xl md:text-5xl font-extrabold font-heading text-slate-900 mb-4 tracking-tight">5 Categories. <span className="font-accent italic text-raspberry-800">One Platform.</span></h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From emergency protocols to conference organization — everything organized into 5 intuitive categories.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Clinical */}
            <div className="group gx-glass rounded-3xl overflow-hidden transition-all duration-300">
              <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Clinical</h3>
                  <span className="text-xs font-semibold text-slate-300 bg-white/10 px-3 py-1 rounded-full">12 Modes</span>
                </div>
                <p className="text-sm text-slate-300 mt-2">Diagnosis, Emergencies, Treatment Protocols</p>
              </div>
              <div className="p-4 space-y-1 max-h-64 overflow-y-auto">
                {['Emergency Protocols', 'OPD Consultation', 'Differential Diagnosis', 'Drug Interactions', 'Latest Guidelines', 'Daily Updates', 'Lab Interpretation', 'Know The Anatomy', 'Surgical Procedures', 'Case Discussion'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => navigate(`/chat?mode=${encodeURIComponent(mode)}`)}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-900 hover:text-white rounded-xl transition-all duration-200 group/item"
                  >
                    <span>{mode}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>

            {/* Patient Care */}
            <div className="group gx-glass rounded-3xl overflow-hidden transition-all duration-300">
              <div className="p-6 bg-gradient-to-br from-rose-700 to-rose-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Patient Care</h3>
                  <span className="text-xs font-semibold text-rose-200 bg-white/10 px-3 py-1 rounded-full">13 Modes</span>
                </div>
                <p className="text-sm text-rose-200 mt-2">Counselling, Explanations, Follow-Ups</p>
              </div>
              <div className="p-4 space-y-1 max-h-64 overflow-y-auto">
                {['Patient Explanation Generator', 'Counselling Scripts', 'Antenatal Care', 'Infertility Management', 'Menopause', 'Diet Plans', 'Lactation Support', 'Birth Plan Counselling', 'Postpartum Care'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => navigate(`/chat?mode=${encodeURIComponent(mode)}`)}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm text-slate-700 hover:bg-rose-600 hover:text-white rounded-xl transition-all duration-200 group/item"
                  >
                    <span>{mode}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>

            {/* Documentation */}
            <div className="group gx-glass rounded-3xl overflow-hidden transition-all duration-300">
              <div className="p-6 bg-gradient-to-br from-amber-600 to-amber-500">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Documentation</h3>
                  <span className="text-xs font-semibold text-amber-200 bg-white/10 px-3 py-1 rounded-full">11 Modes</span>
                </div>
                <p className="text-sm text-amber-100 mt-2">Discharge, Consent, Reports, Certificates</p>
              </div>
              <div className="p-4 space-y-1 max-h-64 overflow-y-auto">
                {['Discharge Summary', 'Consent Forms', 'Referral Letters', 'Medicolegal Help', 'Operative Notes', 'Prescription Writer', 'Insurance Claims', 'Medical Certificates', 'Death Summary', 'Transfer Summary'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => navigate(`/chat?mode=${encodeURIComponent(mode)}`)}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm text-slate-700 hover:bg-amber-500 hover:text-white rounded-xl transition-all duration-200 group/item"
                  >
                    <span>{mode}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>

            {/* Professional */}
            <div className="group gx-glass rounded-3xl overflow-hidden transition-all duration-300">
              <div className="p-6 bg-gradient-to-br from-violet-700 to-violet-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Professional</h3>
                  <span className="text-xs font-semibold text-violet-200 bg-white/10 px-3 py-1 rounded-full">12 Modes</span>
                </div>
                <p className="text-sm text-violet-200 mt-2">CME, Conferences, Research, Publications</p>
              </div>
              <div className="p-4 space-y-1 max-h-64 overflow-y-auto">
                {['Presentation Maker', 'Organize CME', 'Organize Conference', 'Write Article/Chapter', 'PG Exam Revision', 'MRCOG Prep', 'Thesis Writing Help', 'Grant Proposal Writing', 'Social Media Content', 'E-Books Library'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => navigate(`/chat?mode=${encodeURIComponent(mode)}`)}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm text-slate-700 hover:bg-violet-600 hover:text-white rounded-xl transition-all duration-200 group/item"
                  >
                    <span>{mode}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>

            {/* Specialty - Spans 2 columns */}
            <div className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-2xl transition-all duration-300 md:col-span-2">
              <div className="p-6 bg-gradient-to-br from-emerald-700 to-emerald-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Specialty</h3>
                  <span className="text-xs font-semibold text-emerald-200 bg-white/10 px-3 py-1 rounded-full">13 Modes</span>
                </div>
                <p className="text-sm text-emerald-200 mt-2">Subspeciality Areas</p>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-1">
                {['Obstetrics', 'Laparoscopy', 'Hysteroscopy', 'Robotic Surgery', 'Gynec Oncology', 'High Risk Pregnancy', 'Fetal Medicine', 'Urogynaecology', 'Reproductive Medicine', 'Cosmetic Gynecology', 'Adolescent Health'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => navigate(`/chat?mode=${encodeURIComponent(mode)}`)}
                    className="flex items-center justify-between px-4 py-3 text-sm text-slate-700 hover:bg-emerald-600 hover:text-white rounded-xl transition-all duration-200 group/item"
                  >
                    <span>{mode}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-full text-sm font-semibold hover:bg-slate-800 transition-colors shadow-lg hover:shadow-xl"
            >
              Explore All Modes <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
      {/* How it works */}
      <section id="how-it-works" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FAFAF9 0%, #FFFCF8 100%)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-widest uppercase text-raspberry-800 mb-3">Simple Workflow</p>
                        <h2 className="text-4xl font-bold font-heading text-gray-900 mb-4">How Gynovate AI/SI Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {[
              {
                step: '01',
                icon: Monitor,
                title: 'Choose Your Mode',
                desc: 'Select from 51+ specialized clinical modes — Emergency, OPD, ANC, Infertility, Social Media, and more.',
              },
              {
                step: '02',
                icon: MessageCircle,
                title: 'Describe Your Case',
                desc: 'Type your clinical query in any language. No special formatting needed — just describe like you would to a colleague.',
              },
              {
                step: '03',
                icon: Zap,
                title: 'Get Clinical Intelligence',
                desc: 'Receive structured, evidence-based outputs aligned with WHO, ACOG, RCOG, FOGSI and other major guidelines.',
              },
            ].map((step, i) => (
              <div key={step.step} className="relative p-8 rounded-3xl bg-bone border border-gray-100 hover:border-raspberry-200 transition-colors duration-200">
                <div className="text-6xl font-bold font-accent text-slate-300 mb-4 leading-none">{step.step}</div>
                <div className="w-12 h-12 bg-raspberry-50 border border-raspberry-100 rounded-2xl flex items-center justify-center mb-5">
                  <step.icon className="w-6 h-6 text-raspberry-800" />
                </div>
                <h3 className="text-xl font-bold font-heading text-gray-900 mb-3">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 z-10">
                    <ChevronRight className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guidelines section */}
      <section id="about" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-raspberry-800 mb-3">Guideline Engine</p>
              <h2 className="text-4xl font-bold font-heading text-gray-900 mb-6">Evidence-Based, Always</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Every response is aligned with recommendations from the world's leading medical guideline bodies.
                Indian medico-legal compliance is built in from the ground up.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['WHO', 'ACOG', 'RCOG', 'FIGO', 'FOGSI', 'ICMR', 'ESHRE', 'ASRM', 'NAMS', 'NCCN', 'ISUOG', 'MTP Act'].map(org => (
                  <div key={org} className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-raspberry-800 flex-shrink-0" />
                    {org}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Lock, title: 'Medico-Legal Safe', desc: 'All outputs include legal safety notes and documentation templates.' },
                { icon: Globe, title: '60+ Languages', desc: 'Respond in any world language for global clinical practice.' },
                { icon: Zap, title: 'Instant Responses', desc: 'Advanced clinical reasoning by Dr. Vikram Dukle\'s GYNOVATE AI/SI.' },
                { icon: Star, title: 'Research-Backed', desc: 'Cites landmark OBGYN trials: ARRIVE, MAGPIE, WOMAN, PHOENIX & more.' },
              ].map(feature => (
                <div key={feature.title} className="p-6 bg-white rounded-2xl border border-gray-100 hover:border-raspberry-100 hover:shadow-sm transition-all duration-200">
                  <div className="w-10 h-10 bg-raspberry-50 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-raspberry-800" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1.5 font-heading">{feature.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== MEDICAL DISCLAIMER BANNER — above founder story ====== */}
      <section id="disclaimer-banner" className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div
            className="shimmer-sweep relative rounded-3xl overflow-hidden shadow-xl border-2 border-amber-300"
            style={{ background: 'linear-gradient(135deg, #1a0a10 0%, #3a0f1a 55%, #1a0a10 100%)' }}
            data-testid="medical-disclaimer-banner"
          >
            <div className="relative z-[3] p-7 sm:p-9 flex flex-col sm:flex-row gap-6 items-center text-center sm:text-left">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-amber-400 flex items-center justify-center shadow-lg">
                <AlertCircle className="w-8 h-8 text-raspberry-900" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold tracking-widest uppercase text-amber-300 mb-1">
                  Medical Disclaimer
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold font-heading text-white leading-tight mb-2">
                  A Clinical Support Tool — <span className="text-amber-300">Not Medical Advice</span>
                </h3>
                <p className="text-sm sm:text-base text-white/85 leading-snug">
                  Gynovate AI/SI is built for <strong className="text-white">qualified healthcare professionals only</strong>.
                  It provides AI-generated clinical support content — it does NOT diagnose,
                  treat, or replace a doctor's judgment. Every output must be
                  <strong className="text-amber-300"> independently verified</strong> by a registered medical practitioner before use.
                  Not for emergency or life-threatening use.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 items-center">
                  <RouterLink
                    to="/disclaimer"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 hover:text-amber-200 transition-colors"
                    data-testid="disclaimer-link"
                  >
                    Read full disclaimer <ArrowRight className="w-4 h-4" />
                  </RouterLink>
                  <span className="text-white/30">·</span>
                  <RouterLink
                    to="/privacy"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-white/80 hover:text-white transition-colors"
                    data-testid="privacy-link"
                  >
                    Privacy Policy <ArrowRight className="w-4 h-4" />
                  </RouterLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== FOUNDER STORY ====== */}
      <section id="founder-story" className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section header */}
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-widest uppercase text-raspberry-800 mb-3">The Vision Behind the Platform</p>
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-4">Founder Story</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">Built by a Gynaecologist for Gynaecologists.</p>
          </div>

          {/* Story layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            {/* Left: Photo + credentials */}
            <div className="lg:col-span-2 flex flex-col items-center lg:items-start gap-6">
              {/* Hero portrait (primary) */}
              <div className="relative">
                <div className="w-80 rounded-3xl overflow-hidden shadow-2xl border-4 border-[#FDCF33]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_79ca8478-0d38-4691-b5e2-63b4bdfca0c7/artifacts/cqqm3s8l_CEO%20%26%20FOUNDER%20-%20INTIVIV%20%281%29.jpg"
                    alt="Dr. Vikram Dukle - CEO & Founder"
                    className="w-full h-auto object-contain"
                  />
                </div>
                {/* Logo overlay */}
                <div className="absolute -bottom-4 -right-4 w-16 h-16 rounded-2xl overflow-hidden shadow-lg border-2 border-white">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clinical-gyn/artifacts/598nprae_Gynovate%20Logo.jpg"
                    alt="Gynovate AI/SI"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Credentials card */}
              <div className="bg-bone rounded-2xl p-5 border border-gray-100 w-full max-w-xs">
                <h3 className="text-base font-bold text-gray-900 font-heading mb-1">Dr. Vikram Dukle</h3>
                <p className="text-xs text-raspberry-800 font-semibold">Founder - GYNOVATE AI/SI<sup>TM</sup></p>
                <p className="text-xs text-gray-500 mb-3">Candolim, Goa – India</p>
                <div className="space-y-1.5">
                  {[
                    'Laparoscopic/Robotic/Reconstructive Pelvic Surgeon',
                    'Cosmetic Gynaecologist',
                    'Sexual Wellness Educator',
                    'Medical Innovator & Mentor',
                  ].map(item => (
                    <div key={item} className="flex items-start gap-2 text-xs text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#EB5A27] flex-shrink-0 mt-1" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Story text */}
            <div className="lg:col-span-3 space-y-6">
              {/* Opening pull quote */}
              <blockquote className="border-l-4 border-[#FDCF33] pl-6 py-2">
                <p className="text-xl font-accent italic text-gray-800 leading-relaxed">
                  "The future of medicine is not AI replacing doctors — it's AI empowering doctors to be extraordinary."
                </p>
                <footer className="text-sm text-gray-500 mt-2">— Dr. Vikram Dukle, Founder of Gynovate AI/SI</footer>
              </blockquote>

              <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                <p>
                  In the rapidly evolving world of medicine, one challenge remains constant: <strong className="text-gray-900">doctors are overwhelmed.</strong>
                  {' '}Between patient care, documentation, clinical decisions, research updates, and managing clinics,
                  the modern gynaecologist often spends more time on systems than on patients.
                </p>

                <p>
                  Dr. Vikram Dukle — a Laparoscopic/Robotic/Reconstructive Pelvic Surgeon, Cosmetic Gynaecologist,
                  {' '}Sexual Wellness Educator, and Innovator from Goa — witnessed this firsthand during years of
                  clinical practice and teaching.
                </p>

                <p>
                  Currently the <strong className="text-gray-900">President of Panaji Obstetrics and Gynaecological Society</strong> and{' '}
                  <strong className="text-gray-900">Secretary of Goa Chapter of IAGE</strong>.{' '}
                  While training hundreds of gynaecologists and speaking at conferences across India,
                  he repeatedly heard the same concerns:
                </p>
              </div>

              {/* Pain point cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  '"There is no time to keep up with new knowledge."',
                  '"Documentation is exhausting."',
                  '"Technology is complicated."',
                  '"I need AI that understands clinical workflows."',
                ].map((quote, i) => (
                  <div key={i} className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
                    <p className="text-xs text-orange-800 font-medium italic leading-relaxed">{quote}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                <p>
                  Dr. Vikram realized that <strong className="text-gray-900">Artificial Intelligence could transform medical practice</strong> —
                  but only if it was designed specifically <em>for doctors, by doctors.</em>
                </p>
                <p>
                  This vision led to the creation of <strong className="text-raspberry-800">Gynovate AI/SI</strong> —
                  India's first AI ecosystem built exclusively for gynaecologists.
                </p>
              </div>

              {/* Mission statement */}
              <div className="bg-raspberry-800 rounded-2xl p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-raspberry-100 mb-2">The Mission</p>
                <p className="text-base font-semibold leading-relaxed">
                  Empower doctors with intelligent tools that save time, enhance clinical decision-making,
                  improve patient communication, and help doctors grow their practices.
                </p>
              </div>

              {/* What Gynovate combines */}
              <div>
                                <p className="text-sm font-bold text-gray-900 mb-3 font-heading">Gynovate AI/SI combines:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Clinical Intelligence',
                    'Medical Documentation Automation',
                    'Patient Education Tools',
                    'Marketing & Practice Growth',
                    'Continuous Learning for Modern Gynaecologists',
                  ].map(item => (
                    <span key={item} className="text-xs px-3 py-1.5 bg-bone border border-gray-200 rounded-full text-gray-700 font-medium">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Closing vision */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-sm text-gray-600 leading-relaxed">
                                    More than just software, <strong className="text-gray-900">Gynovate AI/SI represents a movement</strong> —
                  helping doctors transition into the era of AI-augmented medicine.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mt-3 font-medium">
                  Dr. Vikram believes the future doctor will not be replaced by AI — but empowered by it.
                                    And <span className="text-raspberry-800 font-semibold">Gynovate AI/SI is built to ensure that gynaecologists lead that future.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PRICING / LAUNCH OFFER ====== */}
      <section id="pricing" className="py-24 gx-pastel-ambient relative overflow-hidden">
        <div className="absolute top-10 right-1/4 w-72 h-72 bg-dawn-rose rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="absolute bottom-20 left-1/4 w-80 h-80 bg-lavender-soft rounded-full blur-3xl opacity-40 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <p className="gx-eyebrow mb-3">Pricing</p>
            <h2 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 mb-4 tracking-tight leading-[1.05]">Simple. And right now &mdash; <span className="font-accent italic text-raspberry-800">free.</span></h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto">Full access to all 51+ clinical modes while we shape the platform with early gynaecologists.</p>
          </div>

          {/* Cost reality bubble — premium shimmering reality-check band */}
          <div
            className="mb-12 rounded-3xl gx-glass overflow-hidden p-6 sm:p-10 relative bg-[linear-gradient(110deg,#FFF5F6,45%,#FAFAF9,55%,#FFF5F6)] bg-[length:200%_100%] animate-shimmer"
            data-testid="cost-comparison-bubble"
          >
            <p className="gx-eyebrow mb-3">Reality Check</p>
            <h3 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 leading-tight tracking-tight">
              Today&rsquo;s gynaecology practice runs on multiple tools — AI, documentation, CME, patient education, mental wellness, and social media.
            </h3>
            <p className="text-base sm:text-lg text-slate-600 mt-4 leading-relaxed">Most doctors end up using <span className="font-bold text-slate-900">10+ different apps</span> every month.</p>
            <p className="text-base sm:text-lg text-slate-700 mt-2 flex items-start gap-1.5">
              <span aria-hidden="true">👉</span>
              <span>The total cost? <span className="font-bold text-raspberry-800">Higher than expected.</span></span>
            </p>

            <div className="mt-7 pt-6 border-t border-slate-200/60">
              <div
                className="shimmer-sweep relative rounded-2xl overflow-hidden px-4 sm:px-6 py-3.5 border border-amber-200 text-center"
                style={{ background: 'linear-gradient(120deg, #fff7e6 0%, #fff0f3 50%, #fde7ee 100%)' }}
                data-testid="reality-check-highlight"
              >
                <p className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-snug relative z-[1] sm:whitespace-nowrap">
                  <span className="text-raspberry-800">Gynovate AI/SI</span> replaces all of that — built only for OB-GYN, and is planned to be{' '}
                  <span className="font-bold text-green-700">98% cheaper</span>.
                </p>
              </div>
            </div>
          </div>

          {/* LAUNCH OFFER — Monumental Free Access Card */}
          <div
            className="relative rounded-[2.5rem] overflow-hidden shadow-[0_20px_80px_-20px_rgba(192,18,60,0.25)] border border-white"
            style={{ background: 'linear-gradient(160deg, #ffffff 0%, #FFFCF8 40%, #FFF5F6 70%, #ffffff 100%)' }}
            data-testid="launch-offer-card"
          >
            {/* Decorative glows */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-100 opacity-60 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-dawn-rose opacity-70 blur-3xl pointer-events-none" />
            <div className="absolute top-1/4 right-1/3 w-40 h-40 rounded-full bg-lavender-soft opacity-40 blur-3xl pointer-events-none" />

            <div className="relative p-10 sm:p-16 text-center">
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-raspberry-800 to-[#7a0e2e] text-white text-[11px] font-black tracking-[0.22em] uppercase mb-6 shadow-lg shadow-raspberry-200" data-testid="launch-offer-pill">
                <Sparkles className="w-3.5 h-3.5" />
                Launch Offer
              </div>

              <h3 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 mb-3 leading-[1.05] tracking-tight">
                Free Access for <span className="font-accent italic text-raspberry-800">Limited time</span>
              </h3>

              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
                Experience the power of AI in your daily practice — completely free.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left mb-10">
                {[
                  'All 51+ Clinical AI Tools',
                  'Instant Drug Dose & Protocol Guidance',
                  'AI Chat for Clinical Decision Support',
                  'Patient Education in 60+ Languages',
                  'WhatsApp-Ready Outputs',
                  'Social Media Manager',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-800">
                    <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-xs font-bold shrink-0 shadow-sm">✓</span>
                    <span className="font-medium">{f}</span>
                  </li>
                ))}
              </ul>

              <div
                className="shimmer-sweep relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 border-2 border-amber-300 text-sm font-bold text-amber-900 mb-10 overflow-hidden shadow-sm"
                data-testid="launch-offer-no-payment"
              >
                <span className="relative z-[1] text-base">🎯</span>
                <span className="relative z-[1]">Free for a limited time — no payment required</span>
              </div>

              <div className="flex flex-col items-center gap-4">
                <button
                  onClick={() => navigate('/signup')}
                  className="relative overflow-hidden px-10 py-4 rounded-2xl bg-gradient-to-br from-raspberry-800 to-[#7a0e2e] text-white text-base font-bold hover:opacity-95 transition-opacity shadow-2xl shadow-raspberry-200 animate-[cta-pulse_2.4s_ease-in-out_infinite]"
                  data-testid="launch-offer-signup-btn"
                >
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none animate-[shimmer_2.8s_ease-in-out_infinite]" />
                  <span className="relative z-[1]">Start Using Gynovate AI/SI — Free</span>
                </button>

                <div className="pt-6 mt-2 border-t border-raspberry-100/60 w-full max-w-2xl">
                  <p className="text-sm text-slate-700 mb-1.5">Before finalizing pricing, I&apos;d love your honest input.</p>
                  <p className="text-sm text-slate-600">
                    Share your feedback and suggestions on{' '}
                    <a
                      href="mailto:gynovateai@gmail.com"
                      className="font-bold text-raspberry-800 hover:text-raspberry-900 underline underline-offset-2"
                      data-testid="launch-offer-feedback-email"
                    >
                      gynovateai@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== MASTERCLASS — JOIN THE COURSE (BIG ICON + PERSUASIVE COPY) ====== */}
      <section id="masterclass" className="py-20 sm:py-24 relative overflow-hidden bg-slate-950">
        {/* ambient glow */}
        <div className="absolute inset-0 pointer-events-none opacity-60"
             style={{
               backgroundImage:
                 'radial-gradient(circle at 18% 28%, rgba(245,158,11,0.22), transparent 45%), radial-gradient(circle at 82% 72%, rgba(192,18,60,0.20), transparent 45%)',
             }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-[1]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left: Big icon + halo */}
            <div className="lg:col-span-5 flex items-center justify-center order-2 lg:order-1">
              <div className="relative">
                {/* Outer ripple rings */}
                <span className="absolute inset-0 rounded-full bg-amber-400/15 blur-3xl scale-150 animate-pulse" />
                <span className="absolute inset-0 rounded-full bg-raspberry-500/15 blur-2xl scale-125" />
                {/* Main icon disk — shimmering medallion */}
                <div
                  className="shimmer-sweep relative w-56 h-56 sm:w-72 sm:h-72 rounded-[2.5rem] flex items-center justify-center shadow-2xl border border-amber-300/30 overflow-hidden"
                  style={{
                    background:
                      'conic-gradient(from 210deg at 50% 50%, #f59e0b 0deg, #fbbf24 80deg, #fde68a 160deg, #f59e0b 240deg, #d97706 320deg, #f59e0b 360deg)',
                  }}
                >
                  <div className="absolute inset-2 rounded-[2rem] bg-gradient-to-br from-amber-50 via-white to-amber-100 flex flex-col items-center justify-center px-4 text-center">
                    {/* New shimmering icon — sparkle starburst */}
                    <div className="relative">
                      <span className="absolute inset-0 rounded-full bg-amber-300/40 blur-xl scale-150 animate-pulse" />
                      <Sparkles
                        className="relative w-20 h-20 sm:w-28 sm:h-28 text-raspberry-800 drop-shadow-lg animate-[cta-pulse_2.6s_ease-in-out_infinite]"
                        strokeWidth={1.4}
                        fill="currentColor"
                      />
                    </div>
                    <p className="mt-3 text-sm sm:text-base font-black tracking-wide uppercase text-raspberry-800 leading-tight">
                      Gynovate AI/SI
                    </p>
                    <p className="text-[11px] sm:text-xs font-bold tracking-[0.3em] uppercase text-amber-700 mt-0.5">
                      Masterclass
                    </p>
                  </div>
                  {/* Floating badges removed — kept clean to prevent clipping */}
                </div>
              </div>
            </div>

            {/* Right: Persuasive copy + CTA */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 mb-4" data-testid="masterclass-pill">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-[11px] font-black tracking-widest uppercase text-amber-300">For OB-GYNs Who Want To Lead</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-white leading-[1.1] mb-5">
                Become the gynaecologist your <span className="text-amber-300">patients trust most</span> — and your peers learn from.
              </h2>

              <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-7 max-w-2xl">
                In 2 days, master the exact AI workflows that save modern OB-GYNs <span className="font-bold text-white">10+ hours every week</span> — sharper diagnoses, instant documentation, smarter patient education, and a clinic that finally runs on autopilot.
              </p>

              {/* Why join — 4 quick value bullets */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Clock, label: 'Save 10+ hrs/week with AI documentation' },
                  { icon: Stethoscope, label: 'Sharper differential diagnosis using guideline-grade prompts' },
                  { icon: Award, label: 'Real workflows you use Monday morning' },
                  { icon: Users, label: 'Private cohort of 500+ ambitious OB-GYNs' },
                ].map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-3 text-sm sm:text-base text-white/85">
                    <span className="mt-0.5 w-7 h-7 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-amber-300" />
                    </span>
                    <span className="leading-snug">{label}</span>
                  </li>
                ))}
              </ul>

              {/* Social proof + scarcity strip */}
              <div className="flex flex-wrap items-center gap-4 mb-7 text-xs sm:text-sm text-white/70">
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1.5">
                    {['#fbbf24', '#f87171', '#a78bfa', '#34d399'].map((c, i) => (
                      <span key={i} className="w-6 h-6 rounded-full border-2 border-slate-950" style={{ background: c }} />
                    ))}
                  </div>
                  <span><span className="font-bold text-white">500+</span> doctors enrolled</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex">
                    {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />)}
                  </div>
                  <span><span className="font-bold text-white">4.9/5</span> from 400+ reviews</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  Next batch: <span className="text-white">31st October &amp; 1st November 2026</span> — limited seats
                </div>
              </div>

              {/* CTA row */}
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://payments.cashfree.com/forms/gynovate-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shimmer-sweep relative inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-base sm:text-lg font-bold text-amber-950 shadow-xl shadow-amber-900/40 overflow-hidden animate-[cta-pulse_2.6s_ease-in-out_infinite]"
                  style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)' }}
                  data-testid="masterclass-join-btn-large"
                >
                  <GraduationCap className="w-5 h-5 relative z-[1]" />
                  <span className="relative z-[1]">Join the Masterclass</span>
                  <ArrowRight className="w-5 h-5 relative z-[1] group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="https://payments.cashfree.com/forms/gynovate-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-4 rounded-2xl text-sm sm:text-base font-semibold text-white border border-white/20 hover:bg-white/5 transition-colors"
                  data-testid="masterclass-curriculum-btn"
                >
                  See Curriculum
                </a>
              </div>

              <p className="mt-4 text-[11px] text-white/50">
                Live + Recorded · Online on Zoom · Built by Dr. Vikram Dukle (AI Generalist)
              </p>
            </div>
          </div>

          {/* ===== GOA IMMERSIVE 2026 — flagship in-person transformation ===== */}
          <div className="mt-14 sm:mt-16" data-testid="goa-immersive-banner">
            <button
              onClick={() => { setGoaImg(0); setGoaOpen(true); }}
              className="group relative w-full text-left rounded-[2rem] overflow-hidden border border-amber-300/30 shadow-2xl shadow-amber-900/30 transition-transform duration-300 hover:scale-[1.01]"
              style={{ background: 'linear-gradient(115deg, #1a1205 0%, #2a1c05 45%, #3a1408 100%)' }}
              data-testid="goa-immersive-open-btn"
            >
              {/* warm sunset glow */}
              <div className="absolute inset-0 pointer-events-none opacity-80" style={{ backgroundImage: 'radial-gradient(circle at 88% 20%, rgba(245,158,11,0.30), transparent 42%), radial-gradient(circle at 10% 90%, rgba(217,70,20,0.25), transparent 45%)' }} />
              <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center p-6 sm:p-9">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 mb-4">
                    <Palmtree className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-[11px] font-black tracking-[0.16em] uppercase text-amber-200">Flagship In-Person Event · Goa</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black font-heading text-white leading-[1.08] mb-3">
                    Join the Immersive Transformation at Goa
                    <span className="block text-amber-300 mt-1">Gynovate AI/SI 2026 · AI Mastery</span>
                  </h3>
                  <p className="text-sm sm:text-base text-white/75 leading-relaxed max-w-2xl mb-5">
                    India's most practical AI course for gynaecologists — 3 days, 100% hands-on, 100+ AI Applications & 10 power-packed masterclasses in Candolim, Goa.
                  </p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                        <Clock className="w-4 h-4 text-amber-300" />
                      </span>
                      <div>
                        <p className="text-[9px] font-black tracking-widest uppercase text-amber-200">Course Dates</p>
                        <p className="text-base font-bold text-white leading-tight">4th – 6th December 2026</p>
                      </div>
                    </div>
                    <GoaCountdown />
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                        <MapPin className="w-4 h-4 text-amber-300" />
                      </span>
                      <div>
                        <p className="text-[9px] font-black tracking-widest uppercase text-amber-200">Venue</p>
                        <p className="text-base font-bold text-white leading-tight">Candolim, Goa</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-amber-950 shadow-lg group-hover:gap-3 transition-all"
                          style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%)' }}>
                      <Sparkles className="w-4 h-4" /> View Course Details
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
                {/* poster peek */}
                <div className="relative hidden md:block w-44 lg:w-52 shrink-0">
                  <img src={GOA_POSTERS[1]} alt="Gynovate AI/SI 2026 Goa details" className="absolute -left-10 top-4 w-full rounded-xl border border-white/10 shadow-xl rotate-[-6deg] opacity-90" />
                  <img src={GOA_POSTERS[0]} alt="Gynovate AI/SI 2026 Goa poster" className="relative w-full rounded-xl border border-amber-300/30 shadow-2xl rotate-[3deg]" />
                  <span className="absolute -bottom-2 -right-2 w-11 h-11 rounded-full bg-amber-400 text-amber-950 text-[10px] font-black flex items-center justify-center text-center leading-none shadow-lg rotate-[3deg]">100+<br/>tools</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* ===== GOA IMMERSIVE POSTER LIGHTBOX ===== */}
      {goaOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="goa-immersive-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setGoaOpen(false)} />
          <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-amber-300">
                <Palmtree className="w-4 h-4" />
                <span className="text-sm font-bold tracking-wide">Gynovate AI/SI 2026 · Goa · 4–6 Dec 2026</span>
              </div>
              <button
                onClick={() => setGoaOpen(false)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="goa-modal-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative flex-1 min-h-0 rounded-2xl overflow-y-auto bg-white/5 border border-white/10">
              <img src={GOA_POSTERS[goaImg]} alt={`Gynovate AI/SI 2026 Goa poster ${goaImg + 1}`} className="w-full h-auto" data-testid="goa-modal-image" />
            </div>
            <div className="flex items-center justify-center gap-3 mt-3">
              {GOA_POSTERS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setGoaImg(i)}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${goaImg === i ? 'bg-amber-400 text-amber-950' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                  data-testid={`goa-modal-tab-${i}`}
                >
                  {i === 0 ? 'Overview' : 'What You Get'}
                </button>
              ))}
              <a
                href="https://wa.me/919850745456?text=Hi!%20I%20want%20to%20register%20for%20GYNOVATE%20AI%202026%20Immersive%20Course%20(4-6%20December%2C%20Goa)"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-black text-white"
                style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
                data-testid="goa-modal-whatsapp-btn"
              >
                WhatsApp 9850745456 — Register
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ====== FUTURE PROMISE ====== */}
      <section className="py-16 bg-gradient-to-br from-raspberry-900 via-[#2a0812] to-raspberry-900 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(253,207,51,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(235,90,39,0.35), transparent 40%)',
          }}
        />
        <div className="max-w-4xl mx-auto px-6 relative z-[2] text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15 mb-5" data-testid="future-promise-pill">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-[11px] font-bold tracking-widest uppercase text-amber-200">What's Coming</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white leading-tight mb-4">
            Built for the future of clinical practice.
          </h2>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
            Expect <span className="text-amber-300 font-semibold">continuous upgrades</span>,
            <span className="text-amber-300 font-semibold"> smarter AI</span>, and
            <span className="text-amber-300 font-semibold"> powerful new tools</span> designed for modern gynaecologists.
          </p>
        </div>
      </section>

      {/* ====== FEATURED IN ====== */}
      <section id="featured-in" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-raspberry-800 mb-3">As Featured In</p>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-gray-900">Trusted by Leading Publications</h2>
          </div>

          {/* Publication logos — auto-scrolling marquee using favicons */}
          <div className="overflow-hidden relative mb-12 py-2" data-testid="publication-marquee">
            <div className="flex gap-6 pub-marquee-track items-center" style={{ width: 'max-content' }}>
              {[...Array(2)].flatMap(() => [
                { name: 'The Entrepreneur Stories', domain: 'theentrepreneurstories.com' },
                { name: 'The Navhind Times', domain: 'navhindtimes.in' },
                { name: 'Republic News India', domain: 'republicnewsindia.com' },
                { name: 'RD Times Health', domain: 'rdtimes.in' },
                { name: 'Hindustan Metro', domain: 'hindustanmetro.com' },
                { name: 'Daily Hunt', domain: 'dailyhunt.in' },
                { name: 'Goa Times', domain: 'goatimes.com' },
                { name: 'The Indian Bulletin', domain: 'theindianbulletin.com' },
                { name: 'Hindustan Saga', domain: 'hindustansaga.in' },
                { name: 'News Outlook', domain: 'newsoutlook.in' },
                { name: 'Bharat Herald', domain: 'bharatherald.in' },
                { name: 'NewsMint24', domain: 'newsmint24.com' },
                { name: 'The Fortune India', domain: 'thefortuneindia.com' },
              ]).map((p, i) => (
                <div
                  key={`${p.domain}-${i}`}
                  className="flex items-center gap-3 flex-shrink-0 px-5 py-4 rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 min-w-[190px] hover:shadow-md transition-shadow"
                  title={p.name}
                >
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${p.domain}&sz=64`}
                    alt={`${p.name} logo`}
                    className="w-7 h-7 rounded flex-shrink-0 object-contain"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-xs font-bold text-gray-700 tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}>
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />
          </div>

          {/* Shimmering article highlight card — a peek into the feature story */}
          <a
            href="https://republicnewsindia.com/leading-the-ai-healthcare-revolution-a-young-doctors-vision-from-goa/"
            target="_blank"
            rel="noopener noreferrer"
            className="shimmer-sweep relative block max-w-4xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-raspberry-900 via-raspberry-800 to-[#4a0815] shadow-2xl group"
            data-testid="featured-article-link"
          >
            <div className="relative z-[3] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
              {/* Left: Publication + headline + excerpt */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src="https://www.google.com/s2/favicons?domain=republicnewsindia.com&sz=64"
                    alt="Republic News India"
                    className="w-6 h-6 rounded bg-white/90 p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-amber-300" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    Republic News India · Feature
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-heading text-white leading-snug mb-3">
                  "Leading the AI Healthcare Revolution — A Young Doctor's Vision from Goa"
                </h3>
                <p className="text-sm text-raspberry-100/90 leading-relaxed mb-4 line-clamp-3">
                  "Dr. Vikram Dukle, a young laparoscopic and cosmetic gynaecologist from Candolim, is quietly rewriting how Indian doctors practise medicine. His platform, Gynovate AI/SI, delivers bedside clinical answers, print-ready documentation, CME content and mental-wellness coaching — all built exclusively for OB-GYN. In an era where foreign AI giants dominate headlines, a doctor from Goa is showing that home-grown, domain-specific intelligence can outcompete them on the one metric that matters: real clinical usefulness…"
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-amber-300 group-hover:text-amber-200 transition-colors">
                  Read the full article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>

              {/* Right: faux "page preview" card */}
              <div className="hidden sm:block w-48 flex-shrink-0 rounded-xl bg-white/95 shadow-xl p-4 relative rotate-[2deg] group-hover:rotate-0 transition-transform">
                <div className="h-1.5 w-10 rounded-full bg-raspberry-700 mb-2" />
                <div className="h-3 w-full rounded bg-gray-800 mb-1.5" />
                <div className="h-3 w-3/4 rounded bg-gray-800 mb-3" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-5/6 rounded bg-gray-300 mb-3" />
                <div className="h-20 w-full rounded bg-gradient-to-br from-raspberry-100 to-raspberry-200 mb-2 flex items-center justify-center">
                  <span className="text-[9px] font-bold text-raspberry-800">GYNOVATE AI/SI</span>
                </div>
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-2/3 rounded bg-gray-300" />
                <span className="absolute top-2 right-2 text-[9px] font-bold text-raspberry-700 bg-amber-200 px-1.5 py-0.5 rounded">LIVE</span>
              </div>
            </div>
          </a>

          {/* Second shimmering article card — The Entrepreneur Stories */}
          <a
            href="https://theentrepreneurstories.com/gynovate-aiindias-first-ai-clinical-co-pilot-for-gynaecologists-by-dr-vikram-dukle/"
            target="_blank"
            rel="noopener noreferrer"
            className="shimmer-sweep relative block max-w-4xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-amber-900 via-amber-800 to-[#3a1d05] shadow-2xl group mt-6"
            data-testid="featured-article-link-entrepreneur"
          >
            <div className="relative z-[3] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src="https://www.google.com/s2/favicons?domain=theentrepreneurstories.com&sz=64"
                    alt="The Entrepreneur Stories"
                    className="w-6 h-6 rounded bg-white/90 p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-amber-200" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    The Entrepreneur Stories · Cover Story
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-heading text-white leading-snug mb-3">
                  {"\u201CGynovate AI/SI \u2014 India\u2019s First AI Clinical Co-Pilot for Gynaecologists, by Dr. Vikram Dukle\u201D"}
                </h3>
                <p className="text-sm text-amber-100/90 leading-relaxed mb-4 line-clamp-3">
                  {"\u201CFrom a busy clinic in Goa, Dr. Vikram Dukle is building what may be India\u2019s most consequential medical AI: a clinical co-pilot designed exclusively for gynaecologists. Gynovate AI/SI is not another chatbot \u2014 it\u2019s a guideline-grade workflow engine with 51+ clinical modes, instant documentation, patient education in 60+ languages, and a private mental-wellness coach for doctors. A quiet, made-in-India answer to ChatGPT, Doximity GPT and Claude \u2014 but built where it matters: the OB-GYN bedside\u2026\u201D"}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-amber-300 group-hover:text-amber-100 transition-colors">
                  Read the full article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>

              {/* Right: faux page preview card with different rotation */}
              <div className="hidden sm:block w-48 flex-shrink-0 rounded-xl bg-white/95 shadow-xl p-4 relative -rotate-[2deg] group-hover:rotate-0 transition-transform">
                <div className="h-1.5 w-10 rounded-full bg-amber-700 mb-2" />
                <div className="h-3 w-full rounded bg-gray-800 mb-1.5" />
                <div className="h-3 w-3/4 rounded bg-gray-800 mb-3" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-5/6 rounded bg-gray-300 mb-3" />
                <div className="h-20 w-full rounded bg-gradient-to-br from-amber-100 to-amber-200 mb-2 flex items-center justify-center">
                  <span className="text-[9px] font-bold text-amber-800">DR. VIKRAM DUKLE</span>
                </div>
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-2/3 rounded bg-gray-300" />
                <span className="absolute top-2 right-2 text-[9px] font-bold text-amber-700 bg-amber-200 px-1.5 py-0.5 rounded">NEW</span>
              </div>
            </div>
          </a>

          {/* Third shimmering article card — The Navhind Times */}
          <a
            href="https://navhindtimes.in/buzz/rethinking-a-doctors-workload/"
            target="_blank"
            rel="noopener noreferrer"
            className="shimmer-sweep relative block max-w-4xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-teal-900 via-teal-800 to-[#042f2e] shadow-2xl group mt-6"
            data-testid="featured-article-link-navhind"
          >
            <div className="relative z-[3] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src="https://www.google.com/s2/favicons?domain=navhindtimes.in&sz=64"
                    alt="The Navhind Times"
                    className="w-6 h-6 rounded bg-white/90 p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-teal-200" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    The Navhind Times · NT Buzz
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-heading text-white leading-snug mb-3">
                  {"\u201CRethinking a Doctor\u2019s Workload\u201D"}
                </h3>
                <p className="text-sm text-teal-100/90 leading-relaxed mb-4 line-clamp-3">
                  {"\u201CCandolim-based gynaecologist Dr. Vikram Dukle believes AI can ease the growing administrative burden of modern practice. Gynovate combines clinical support, documentation tools and a mental wellness companion for doctors \u2014 following FOGSI and international guidelines, supporting voice input in 60+ languages including Konkani, and already helping hundreds of doctors in small towns and Tier III cities. \u2018By the time you finish your procedure, you already have a summary of the whole operation\u2026\u2019\u201D"}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-teal-300 group-hover:text-teal-100 transition-colors">
                  Read the full article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
              <div className="hidden sm:block w-48 flex-shrink-0 rounded-xl bg-white/95 shadow-xl p-4 relative rotate-[2deg] group-hover:rotate-0 transition-transform">
                <div className="h-1.5 w-10 rounded-full bg-teal-700 mb-2" />
                <div className="h-3 w-full rounded bg-gray-800 mb-1.5" />
                <div className="h-3 w-3/4 rounded bg-gray-800 mb-3" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-5/6 rounded bg-gray-300 mb-3" />
                <div className="h-20 w-full rounded bg-gradient-to-br from-teal-100 to-teal-200 mb-2 flex items-center justify-center">
                  <span className="text-[9px] font-bold text-teal-800">THE NAVHIND TIMES</span>
                </div>
                <div className="h-1.5 w-full rounded bg-gray-300 mb-1" />
                <div className="h-1.5 w-2/3 rounded bg-gray-300" />
                <span className="absolute top-2 right-2 text-[9px] font-bold text-teal-700 bg-teal-200 px-1.5 py-0.5 rounded">PRESS</span>
              </div>
            </div>
          </a>

          {/* Two-up press cards — RD Times Health + Hindustan Metro */}
          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6 mt-6">
            <a
              href="https://health.rdtimes.in/leading-the-ai-healthcare-revolution-a-young-doctors-vision-from-goa/"
              target="_blank"
              rel="noopener noreferrer"
              className="shimmer-sweep relative block rounded-3xl overflow-hidden bg-gradient-to-br from-rose-900 via-rose-800 to-[#3b0716] shadow-xl group p-6"
              data-testid="featured-article-link-rdtimes"
            >
              <div className="relative z-[3]">
                <div className="flex items-center gap-2.5 mb-3">
                  <img
                    src="https://www.google.com/s2/favicons?domain=rdtimes.in&sz=64"
                    alt="RD Times Health"
                    className="w-5 h-5 rounded bg-white/90 p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-rose-200" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    RD Times Health · Feature
                  </span>
                </div>
                <h3 className="text-lg font-bold font-heading text-white leading-snug mb-2.5">
                  {"\u201CLeading the AI Healthcare Revolution \u2014 A Young Doctor\u2019s Vision from Goa\u201D"}
                </h3>
                <p className="text-[13px] text-rose-100/85 leading-relaxed mb-3 line-clamp-4">
                  {"\u201CA Goa Medical College alumnus chosen to lead the Panaji Obstetrics & Gynaecological Society, Dr. Vikram Dukle is bringing clinical work, entrepreneurship and technology together \u2014 Gynovate is described as India\u2019s first AI course for healthcare entrepreneurs and Copilot for Gynaecologists\u2026\u201D"}
                </p>
                <span className="inline-flex items-center gap-2 text-[13px] font-bold text-rose-300 group-hover:text-rose-100 transition-colors">
                  Read the full article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </a>
            <a
              href="https://www.hindustanmetro.com/leading-the-ai-healthcare-revolution-a-young-doctors-vision-from-goa"
              target="_blank"
              rel="noopener noreferrer"
              className="shimmer-sweep relative block rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-900 via-indigo-800 to-[#141033] shadow-xl group p-6"
              data-testid="featured-article-link-hindustanmetro"
            >
              <div className="relative z-[3]">
                <div className="flex items-center gap-2.5 mb-3">
                  <img
                    src="https://www.google.com/s2/favicons?domain=hindustanmetro.com&sz=64"
                    alt="Hindustan Metro"
                    className="w-5 h-5 rounded bg-white/90 p-0.5"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-200" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    Hindustan Metro · News
                  </span>
                </div>
                <h3 className="text-lg font-bold font-heading text-white leading-snug mb-2.5">
                  {"\u201CLeading the AI Healthcare Revolution \u2014 A Young Doctor\u2019s Vision from Goa\u201D"}
                </h3>
                <p className="text-[13px] text-indigo-100/85 leading-relaxed mb-3 line-clamp-4">
                  {"\u201CThe AI Copilot supports prescriptions, summaries, notes and decision-making in real time. For doctors with heavy patient loads and constant pressure, that kind of help matters \u2014 with ethical, safe and responsible AI use and patient trust at the center. Healthcare should not wait for the future; it should build it now, with doctors leading the way\u2026\u201D"}
                </p>
                <span className="inline-flex items-center gap-2 text-[13px] font-bold text-indigo-300 group-hover:text-indigo-100 transition-colors">
                  Read the full article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-raspberry-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-heading text-white mb-6">
            Ready to Elevate Your Practice?
          </h2>
          <p className="text-lg text-raspberry-100 mb-10 max-w-2xl mx-auto">
                        Join clinicians worldwide using GYNOVATE AI/SI for smarter decisions,
            faster documentation, and better patient communication.
          </p>
          <button
            data-testid="cta-launch-btn"
            onClick={() => navigate('/signup')}
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-raspberry-900 rounded-full text-base font-bold hover:-translate-y-1 transition-transform duration-200 shadow-xl"
          >
            Open AI/SI Copilot
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-5 text-sm text-raspberry-200">51+ clinical modes across obstetrics, gynaecology, and subspecialties.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 text-gray-400">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_clinical-gyn/artifacts/598nprae_Gynovate%20Logo.jpg"
                  alt="Gynovate AI/SI"
                  className="h-8 w-8 rounded-lg object-cover"
                />
                                <span className="text-white font-bold font-heading text-sm">Gynovate AI/SI</span>
              </div>
            </div>
            <p className="text-xs text-center max-w-sm">
              Created by India's leading Cosmetic Gynaecologist and Innovator –
              <strong className="text-white"> Dr. Vikram Dukle</strong>, Candolim, Goa – India.
              AI outputs are decision support, not a replacement for clinical judgement.
            </p>
                <div className="flex items-center gap-4 text-xs">
                  <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                  <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
                  <span>© 2026 Gynovate AI/SI</span>
                </div>
          </div>
        </div>
      </footer>
      {/* UpgradeModal disabled during launch (free access for early gynaecologists) */}
    </div>
  );
}
